import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Linkedin,
  Mail,
  Globe,
} from "lucide-react";

/* ------- Hero photo (cropped from the Martha's Vineyard mockup) ------- */
function VineyardHeroPhoto() {
  return (
    <div className="relative overflow-hidden rounded-[28px] shadow-[0_30px_80px_-40px_rgba(90,50,180,0.5)] ring-1 ring-white/40">
      <img
        src="/vineyard/hero.jpg"
        alt="Martha's Vineyard Victorian cottage with purple hydrangeas along a white picket fence"
        className="w-full h-auto block"
        loading="eager"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#F7F3FB]/25 via-transparent to-transparent" />
    </div>
  );
}

function MonogramMark({ size = 40 }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="mm" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9269F1" />
          <stop offset="100%" stopColor="#5A2FD6" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="10" fill="url(#mm)" />
      <g fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round">
        <ellipse cx="15" cy="20" rx="6.5" ry="9" />
        <ellipse cx="25" cy="20" rx="6.5" ry="9" />
      </g>
    </svg>
  );
}

function HydrangeaIcon() {
  return (
    <svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
      <circle cx="20" cy="22" r="14" fill="#D3BCF3" opacity="0.7" />
      <g fill="#6D3BD6" opacity="0.85">
        <circle cx="14" cy="18" r="3" />
        <circle cx="24" cy="16" r="3" />
        <circle cx="26" cy="24" r="3" />
        <circle cx="16" cy="26" r="3" />
        <circle cx="20" cy="21" r="2.4" />
      </g>
      <ellipse cx="20" cy="34" rx="8" ry="2" fill="#7EA07C" opacity="0.4" />
    </svg>
  );
}

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export default function Vineyard() {
  return (
    <main
      data-testid="vineyard-page"
      className="min-h-screen bg-[#F7F3FB] text-[#2A1454] font-body"
    >
      {/* Top nav */}
      <header className="mx-auto max-w-[1200px] px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="vineyard-logo">
          <MonogramMark />
          <span className="font-display font-extrabold text-lg text-[#2A1454]">
            CMD<span className="text-[#6D3BD6]">+</span>DesignLab
            <sup className="align-super text-[0.5em] text-[#6D3BD6] ml-0.5">
              &reg;
            </sup>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#2A1454]">
          <a href="#solutions" className="hover:text-[#6D3BD6] transition-colors">
            Solutions
          </a>
          <a href="#about" className="hover:text-[#6D3BD6] transition-colors">
            About
          </a>
          <a href="#connect" className="hover:text-[#6D3BD6] transition-colors">
            Connect
          </a>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative mx-auto max-w-[1200px] px-6 md:px-10 pt-6 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          className="lg:col-span-7"
          initial="initial"
          animate="animate"
          variants={{
            animate: { transition: { staggerChildren: 0.12 } },
          }}
        >
          <motion.h1
            variants={fade}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2A1454]"
          >
            Nice meeting you on the Vineyard.
          </motion.h1>
          <motion.p
            variants={fade}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-8 text-lg max-w-md leading-relaxed text-[#4a3a70]"
          >
            Strategy. Creativity. Technology. Built to make organizations work
            smarter.
          </motion.p>
          <motion.div
            variants={fade}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8"
          >
            <div className="h-px w-24 bg-[#6D3BD6]/40" />
            <p className="mt-5 text-sm text-[#4a3a70]">
              Explore the solutions we talked about.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#commconnect"
                data-testid="hero-cta-commconnect"
                className="inline-flex items-center gap-2 rounded-full bg-[#6D3BD6] hover:bg-[#5A2FD6] text-white px-6 py-3 font-medium transition-colors shadow-[0_10px_30px_-12px_rgba(90,50,180,0.6)]"
              >
                Explore CommConnect <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#retainproof"
                data-testid="hero-cta-retainproof"
                className="inline-flex items-center gap-2 rounded-full border border-[#6D3BD6]/30 text-[#2A1454] bg-white/70 hover:bg-white px-6 py-3 font-medium transition-colors"
              >
                Explore RetainProof <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#6D3BD6]">
              <MapPin className="h-3.5 w-3.5" /> Martha&#39;s Vineyard &middot; August 2026
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
        >
          <VineyardHeroPhoto />
        </motion.div>
      </section>

      {/* Solutions */}
      <section
        id="solutions"
        className="mx-auto max-w-[1200px] px-6 md:px-10 pb-16 grid md:grid-cols-2 gap-6"
      >
        {/* CommConnect */}
        <motion.div
          id="commconnect"
          data-testid="card-commconnect"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl bg-white p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(90,50,180,0.35)] scroll-mt-24"
        >
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-[#9269F1] to-[#5A2FD6] text-white text-2xl font-serif font-bold shadow-md">
              C
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2A1454]">
              CommConnect
              <sup className="align-super text-[0.4em] text-[#6D3BD6] ml-1">
                &trade;
              </sup>
            </h2>
          </div>
          <p className="mt-6 text-[#4a3a70] leading-relaxed">
            A centralized communications operations platform for chapters,
            associations, nonprofits and member-based organizations.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#4a3a70]">
            {[
              "Requests",
              "Leadership Approvals",
              "Committee Routing",
              "Revisions",
              "Status Tracking",
            ].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#6D3BD6]" /> {t}
              </span>
            ))}
          </div>
          <a
            href="https://mycommconnect.com"
            target="_blank"
            rel="noreferrer"
            data-testid="cta-view-demo"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#6D3BD6] hover:bg-[#5A2FD6] text-white px-6 py-3 font-medium transition-colors"
          >
            View Demo <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>

        {/* RetainProof */}
        <motion.div
          id="retainproof"
          data-testid="card-retainproof"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="rounded-3xl bg-white p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(90,50,180,0.35)] scroll-mt-24"
        >
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-[#9269F1] to-[#5A2FD6] text-white text-2xl font-serif font-bold shadow-md">
              R<span className="text-base align-super">+</span>
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2A1454]">
              RetainProof
              <sup className="align-super text-[0.4em] text-[#6D3BD6] ml-1">
                &trade;
              </sup>
            </h2>
          </div>
          <p className="mt-6 text-[#4a3a70] leading-relaxed">
            Independent marketing performance intelligence that connects agency
            and vendor spending to retained cases and true marketing ROI.
          </p>
          <div className="mt-6 rounded-2xl bg-[#F1EAFB] p-5 flex items-center gap-4">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-[#6D3BD6]">
              <HydrangeaIcon />
            </span>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#6D3BD6]">
                Vineyard Introduction
              </div>
              <div className="text-sm text-[#2A1454] mt-1">
                Marketing Performance Assessment:{" "}
                <strong className="font-semibold">$750</strong>
              </div>
            </div>
          </div>
          <a
            href="mailto:hello@cmddesignlab.com?subject=RetainProof%20Vineyard%20Introduction"
            data-testid="cta-book-conversation"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#6D3BD6] hover:bg-[#5A2FD6] text-white px-6 py-3 font-medium transition-colors"
          >
            Book a Conversation <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>
      </section>

      {/* Also from */}
      <section
        id="about"
        className="mx-auto max-w-[1200px] px-6 md:px-10 pb-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl bg-gradient-to-r from-white to-[#F1EAFB] p-6 md:p-8 flex flex-col md:flex-row items-center gap-6"
        >
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white shadow">
            <HydrangeaIcon />
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-2xl md:text-3xl text-[#2A1454]">
              Also from CMD+DesignLab.
            </h3>
            <p className="text-[#4a3a70] mt-1">
              Creative strategy, digital marketing, branding, web experiences
              and business technology.
            </p>
          </div>
          <Link
            to="/"
            data-testid="cta-explore-designlab"
            className="inline-flex items-center gap-2 rounded-full border border-[#6D3BD6]/30 text-[#2A1454] bg-white hover:bg-[#F1EAFB] px-6 py-3 font-medium transition-colors"
          >
            Explore DesignLab <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>

      {/* Connect / Footer */}
      <footer
        id="connect"
        className="mx-auto max-w-[1200px] px-6 md:px-10 pb-14"
      >
        <div className="rounded-3xl bg-gradient-to-r from-[#EFE4FA] to-[#F7F0FC] p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#9269F1] to-[#5A2FD6] grid place-items-center text-white font-serif text-lg shadow-md">
              MD
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#6b5c8c]">
                Created by
              </div>
              <div className="font-serif text-xl text-[#2A1454] mt-0.5">
                C. Maria DeVoe
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-sm text-[#2A1454]">
            <a
              href="https://www.linkedin.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 hover:text-[#6D3BD6] transition-colors"
              data-testid="footer-linkedin"
            >
              <Linkedin className="h-4 w-4 text-[#6D3BD6]" /> LinkedIn
            </a>
            <a
              href="mailto:hello@cmddesignlab.com"
              className="inline-flex items-center gap-2 hover:text-[#6D3BD6] transition-colors"
              data-testid="footer-email"
            >
              <Mail className="h-4 w-4 text-[#6D3BD6]" /> Email
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 hover:text-[#6D3BD6] transition-colors"
              data-testid="footer-visit"
            >
              <Globe className="h-4 w-4 text-[#6D3BD6]" /> Visit CMD+DesignLab
            </Link>
          </div>
          <div className="opacity-80">
            <MonogramMark size={56} />
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-[#6b5c8c]">
          © {new Date().getFullYear()} CMD+DesignLab&reg;. Vineyard edition.
        </div>
      </footer>
    </main>
  );
}
